import { Banner, DropdownButton, LoadingSpinner, Menu, SegmentedControl, Typography } from '@neo4j-ndl/react';
import { TableCellsIconOutline } from '@neo4j-ndl/react/icons';
import { fleetManagementApi, useActiveProject, useSetting } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { BackButton, DataGridHelpers, SearchField } from '@nx/ui';
import { useRef, useState } from 'react';
import { useParams } from 'react-router';

import { ExploreIconOutline } from '../../../../upx/src/components/icons';
import { withUpxContext } from '../../shared/components/upx-context';
import { DeploymentGraph } from '../graph/deployments-graph';
import withProjectCapabilityGuard from '../shared/components/project-capability-guard';
import { DbmsDetailsTable } from './deployment-details-table';
import { DEFAULT_TAB, TAB_OPTIONS } from './types';

const DbmsDetailsView = () => {
  const [filter, setFilter] = useState('');
  const [selectedTab, setSelectedTab] = useState(DEFAULT_TAB);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const activeProject = useActiveProject();
  const { deploymentId } = useParams<'deploymentId'>();
  const [deploymentDisplayType, setDeploymentDisplayType] = useSetting('ops', 'deploymentDetailsDisplay');

  const { data, isLoading, error } = fleetManagementApi.useGetDeploymentDetailsQuery(
    {
      projectId: activeProject.id,
      deploymentId: deploymentId ?? '',
    },
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      pollingInterval: 15000,
      skip: isNullish(deploymentId),
    },
  );

  if (isLoading) {
    return <LoadingSpinner size="large" className="relative left-1/2 top-1/2" />;
  }

  if (error) {
    return (
      <Banner type="danger" usage="inline" className="m-4">
        {String(error)}
      </Banner>
    );
  }

  return (
    <div className="mt-4 overflow-auto sm:mx-4 md:mx-8 xl:mx-auto xl:max-w-[1680px] xl:px-8">
      <BackButton to="../instances/?instancesTab=self-managed" />
      <div className="mb-6 flex items-center justify-between px-2 pt-2">
        <Typography variant="h4">{data?.name}</Typography>
        <SegmentedControl
          onChange={(changedId) => setDeploymentDisplayType(changedId)}
          hasOnlyIcons
          size="small"
          selected={deploymentDisplayType}
        >
          <SegmentedControl.Item
            value="list"
            htmlAttributes={{
              'aria-label': 'View details in table view',
            }}
          >
            <TableCellsIconOutline className="size-4" />
          </SegmentedControl.Item>
          <SegmentedControl.Item
            value="graph"
            htmlAttributes={{
              'aria-label': 'View details in graph view',
            }}
          >
            <ExploreIconOutline className="size-4" />
          </SegmentedControl.Item>
        </SegmentedControl>
      </div>

      {deploymentDisplayType === 'list' && (
        <DataGridHelpers.Wrapper>
          <DataGridHelpers.OuterHeader>
            <div className="flex w-full flex-wrap justify-between gap-2">
              <div className="flex w-full gap-2">
                <div className="flex basis-[400px] gap-2">
                  <SearchField
                    className="min-w-36 grow"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                    htmlAttributes={{
                      'aria-label': 'Search for deployments elements',
                    }}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <DropdownButton
                    aria-label="Toggle menu"
                    size="medium"
                    ref={menuRef}
                    isOpen={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    className="border-none"
                  >
                    <div className="flex flex-1 items-center gap-1">
                      <Typography variant="body-medium" className="text-palette-neutral-text-weaker">
                        View:
                      </Typography>
                      <Typography variant="body-medium">
                        {TAB_OPTIONS.find((option) => option.value === selectedTab)?.label}
                      </Typography>
                    </div>
                  </DropdownButton>
                </div>
                <Menu isOpen={isOpen} anchorRef={menuRef} onClose={() => setIsOpen(false)}>
                  <Menu.Items>
                    {TAB_OPTIONS.map((option) => (
                      <Menu.Item key={option.value} title={option.label} onClick={() => setSelectedTab(option.value)} />
                    ))}
                  </Menu.Items>
                </Menu>
              </div>
            </div>
          </DataGridHelpers.OuterHeader>

          {selectedTab === 'SERVERS' && (
            <DbmsDetailsTable
              dataGridType={'SERVERS'}
              details={data?.dbms?.servers ?? []}
              filter={filter}
              setFilter={setFilter}
            />
          )}
          {selectedTab === 'DATABASES' && (
            <DbmsDetailsTable
              dataGridType={'DATABASES'}
              details={data?.dbms?.databases ?? []}
              filter={filter}
              setFilter={setFilter}
            />
          )}
          {selectedTab === 'DATABASE_INSTANCES' && (
            <DbmsDetailsTable
              dataGridType={'DATABASE_INSTANCES'}
              details={data?.dbms?.servers.flatMap((server) => server.databaseInstances) ?? []}
              filter={filter}
              setFilter={setFilter}
            />
          )}
        </DataGridHelpers.Wrapper>
      )}

      {deploymentDisplayType === 'graph' && isNotNullish(data) && (
        <DeploymentGraph
          deployments={[data]}
          searchFilter=""
          containerClassName={'h-[calc(100vh-188px)]'}
          isInDetailsView={true}
        />
      )}
    </div>
  );
};

const DbmsDetailsWithUpxContext = withUpxContext(withProjectCapabilityGuard(DbmsDetailsView));

export default DbmsDetailsWithUpxContext;
